import cx from 'classnames';
import {
  useCallback,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';
import { useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { isMemberLoggedIn } from '@trello/authentication';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import { useRecentBoards } from '@trello/recent-boards';
import { navigate } from '@trello/router/navigate';
import { boardUrlFromShortLink } from '@trello/urls';

import * as styles from './SettingsFullPage.module.less';

export const SettingsFullPage: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  /**
   * When the user closes the settings page, we want to navigate back to the board
   * they were last on. This is tracked by the recent-boards package, and backed up
   * to local storage. If there are no recent boards, we navigate to the home page.
   */
  const { data } = useRecentBoards();
  const intl = useIntl();
  const onClose = useCallback(() => {
    const latestBoard = data?.boards[0];
    const returnUrl = latestBoard
      ? boardUrlFromShortLink(latestBoard.shortLink, latestBoard.name)
      : '/';

    Analytics.sendClickedButtonEvent({
      buttonName: 'closeSettingsModalButton',
      source: 'settingsModal',
      attributes: {
        returnBoardId: latestBoard?.id,
      },
    });

    navigate(returnUrl, { trigger: true, replace: true });
  }, [data?.boards]);

  return (
    <div
      className={cx(styles.page, {
        [styles.anonymous]: !isMemberLoggedIn(),
      })}
    >
      <main className={styles.main}>
        <Button
          className={styles.closeButton}
          aria-label={intl.formatMessage({
            id: 'templates.settings_full_page.close',
            defaultMessage: 'Close',
            description: 'Close button for settings modal',
          })}
          iconBefore={<CloseIcon />}
          onClick={onClose}
        />
        {children}
      </main>
    </div>
  );
};
